import { Suspense } from 'react';
import type { ComponentType, LazyExoticComponent, ReactElement } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { PanelGuard } from '@favelafood/shared/auth/PanelGuard';
import { lazyWithReload } from '@favelafood/shared/lib/lazyWithReload';
import App from './App';
import { MerchantShell } from './shell/MerchantShell';

// Área Comerciante (dono da loja) + KDS. Base '/', sem prefixo de rota.
// KDS — Kitchen Display System, kiosk full-screen FORA do shell (RF-002).
const Kds = lazyWithReload(() => import('./kds/KdsPage'));

// Página PÚBLICA de aceite do convite (fora do PanelGuard — visitante anônimo).
const InviteAcceptPage = lazyWithReload(() => import('./convite/InviteAcceptPage'));

// Owner — resumo da loja + catálogo (Fase 3b) + pedidos (F3 do PLAN).
const OwnerHome = lazyWithReload(() => import('./owner/OwnerHome'));
const PedidosPage = lazyWithReload(() => import('./owner/orders/PedidosPage'));
const FinanceiroPage = lazyWithReload(() => import('./owner/finance/FinanceiroPage'));
const OrderDetailPage = lazyWithReload(() => import('./owner/orders/OrderDetailPage'));
const ProdutosPage = lazyWithReload(() => import('./owner/catalog/ProdutosPage'));
const CategoriasPage = lazyWithReload(() => import('./owner/catalog/CategoriasPage'));
const ProductEditorPage = lazyWithReload(() => import('./owner/catalog/ProductEditorPage'));
const OpcionaisEditor = lazyWithReload(() => import('./owner/catalog/OpcionaisEditor'));
const PromotionsPage = lazyWithReload(() => import('./owner/promotions/PromotionsPage'));
const ReviewsPage = lazyWithReload(() => import('./owner/reviews/ReviewsPage'));

/**
 * Wraps a lazy-loaded page in `<Suspense>` so the router can render a fallback
 * while the chunk is fetched.
 */
const lazyRoute = (Comp: LazyExoticComponent<ComponentType>): ReactElement => (
  <Suspense fallback={<div className="p-8 text-muted">Carregando...</div>}>
    <Comp />
  </Suspense>
);

/**
 * Browser router for the comerciante (store owner) panel (base '/').
 *
 * A single root `PanelGuard(['merchant','admin'])` protects everything. No login
 * route: an unauthenticated visit → PanelGuard → `ensureKeycloakLogin()` →
 * Keycloak (the old password-grant `MerchantLoginPage` died with the C3 carve).
 *
 * The KDS is a sibling of the owner shell, NOT a child: it's a full-screen
 * kiosk, so it renders directly under the guard without the sidebar/topbar.
 * Owner routes live inside `MerchantShell`; literals precede the `:id` matchers.
 */
export const routeConfig: RouteObject[] = [
  {
    element: <App />,
    children: [
      // PÚBLICA (antes do guard): aceite do convite de ativação enviado pelo staff.
      { path: '/convite/:token', element: lazyRoute(InviteAcceptPage) },
      {
        element: (
          <PanelGuard allowed={['merchant', 'admin']}>
            <Outlet />
          </PanelGuard>
        ),
        children: [
          // KDS kiosk — outside the shell.
          { path: '/kds', element: lazyRoute(Kds) },
          // Owner shell — resumo + catálogo.
          {
            path: '/',
            element: <MerchantShell />,
            children: [
              { index: true, element: lazyRoute(OwnerHome) },
              // Operação — pedidos com ações + rastreio do entregador.
              { path: 'pedidos', element: lazyRoute(PedidosPage) },
              { path: 'financeiro', element: lazyRoute(FinanceiroPage) },
              { path: 'pedidos/:orderId', element: lazyRoute(OrderDetailPage) },
              // Literal 'novo' antes do matcher ':id'.
              { path: 'produtos', element: lazyRoute(ProdutosPage) },
              { path: 'produtos/novo', element: lazyRoute(ProductEditorPage) },
              { path: 'produtos/:id', element: lazyRoute(ProductEditorPage) },
              { path: 'produtos/:id/opcionais', element: lazyRoute(OpcionaisEditor) },
              { path: 'categorias', element: lazyRoute(CategoriasPage) },
              { path: 'promocoes', element: lazyRoute(PromotionsPage) },
              { path: 'avaliacoes', element: lazyRoute(ReviewsPage) },
            ],
          },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routeConfig);
